import React, { useState } from "react";
import { TrendingUp, Eye, type LucideIcon } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { formatCompactNumber } from "@/lib/utils/formatNumber";
import { Skeleton } from "@/components/ui/skeleton";
import type { CampaignPerformanceDataPoint } from "../../types/analytics.types";

type MetricKey = "reach" | "engagement" | "views" | "impressions";

const METRIC_OPTIONS: { key: MetricKey; label: string; icon: LucideIcon }[] = [
  { key: "engagement", label: "Engagement", icon: TrendingUp },
  { key: "views", label: "Views", icon: Eye },
];

export interface CampaignPerformanceChartProps {
  data: CampaignPerformanceDataPoint[];
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  title?: string;
}

/**
 * Line chart displaying campaign performance metrics over time.
 * Supports toggling between Reach, Engagement, Views, and Impressions.
 * Includes loading, error, and empty states.
 */
export const CampaignPerformanceChart: React.FC<CampaignPerformanceChartProps> = ({
  data,
  isLoading = false,
  isError = false,
  onRetry,
  title = "Campaign Performance",
}) => {
  const [metric, setMetric] = useState<MetricKey>("engagement");

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <Skeleton className="h-6 w-48 mb-6" />
        <Skeleton className="h-[300px] w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">{title}</h2>
        <div className="flex flex-col items-center justify-center h-[300px] text-gray-500">
          <p className="mb-4">Failed to load performance data.</p>
          {onRetry && (
            <button
              onClick={onRetry}
              className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">{title}</h2>
        <div className="flex items-center justify-center h-[300px] text-gray-400">
          No data available for this campaign.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        <div className="flex items-center space-x-3">
          {METRIC_OPTIONS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setMetric(key)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl border transition-all duration-200 ${
                metric === key
                  ? "bg-[#F9F5FF] border-[#E9D7FE] text-[#7F22FE] shadow-sm"
                  : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300"
              }`}
            >
              <Icon
                className={`w-4 h-4 ${
                  metric === key ? "text-[#7F22FE]" : "text-gray-700"
                }`}
              />
              <span>{label}</span>
            </button>
          ))}
        </div>
      </div>

      <div aria-label={`Campaign performance chart showing ${metric} over time`}>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis tickFormatter={(v: number) => formatCompactNumber(v)} />
            <Tooltip
              formatter={(value: number) => [formatCompactNumber(value), metric]}
            />
            <Line
              type="monotone"
              dataKey={metric}
              stroke="#8b5cf6"
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>

        {/* Visually hidden table for screen readers */}
        <table className="sr-only">
          <caption>Campaign performance data — {metric}</caption>
          <thead>
            <tr>
              <th>Month</th>
              <th>{metric}</th>
            </tr>
          </thead>
          <tbody>
            {data.map((point) => (
              <tr key={point.name}>
                <td>{point.name}</td>
                <td>{point[metric]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CampaignPerformanceChart;
